import {
  faCircleInfo,
  faCirclePlus,
  faCloudArrowUp,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { MultipleMediaUpload } from "services/apiService";

const MediaLibraryCreate: React.FC = () => {
  const [images, setImages] = useState<File[]>([]);
  const [message, setMessage] = useState<{
    type: "success" | "error" | "";
    text: string;
  }>({ type: "", text: "" });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const allowedExtensions = ["jpg", "jpeg", "png", "gif", "mp4"];

  const isValidFile = (file: File) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    return extension ? allowedExtensions.includes(extension) : false;
  };

  const handleUploadFile = async () => {
    if (images.length === 0) {
      toast.error("Please upload at least one image.");
      return;
    }

    const formData = new FormData();
    images.forEach((image) => {
      if (!isValidFile(image)) {
        setMessage({
          type: "error",
          text: "Invalid file type. Allowed types are: jpg, jpeg, png, gif, mp4.",
        });
        return;
      }
      formData.append("file_name", image);
    });
    try {
      const response = await MultipleMediaUpload(formData);
      if (response.status_code === 200) {
        toast.success("Uploaded successfully");
        setMessage({ type: "success", text: "Images uploaded successfully!" });
        navigate("/media-list");
      } else {
        setMessage({
          type: "error",
          text: response.message || "Error uploading the images.",
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Error uploading the images." });
    }
  };

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files); // Allow multiple files
    const validFiles = files.filter((file) => isValidFile(file));

    if (validFiles.length > 0) {
      setImages((prevImages) => [...prevImages, ...validFiles]);
      setMessage({ type: "", text: "" });
    } else {
      setMessage({
        type: "error",
        text: "Invalid file type. Allowed types are: jpg, jpeg, png, gif, mp4.",
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleRemoveImage = (index: number) => {
    setImages((prevImages) => {
      const updatedImages = prevImages.filter((_, i) => i !== index);
      if (updatedImages.length === 0 && fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return updatedImages;
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const validFiles = files.filter((file) => isValidFile(file));

    if (validFiles.length > 0) {
      setImages((prevImages) => [...prevImages, ...validFiles]);
      setMessage({ type: "", text: "" });
    } else {
      setMessage({
        type: "error",
        text: "Invalid file type. Allowed types are: jpg, jpeg, png, gif, mp4.",
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleContainerClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <div style={{ maxWidth: "100%", margin: "0 auto", padding: "20px" }}>
        <div
          style={{
            backgroundColor: "#f9f9c5",
            color: "#333",
            padding: "10px",
            border: "1px solid #e6e600",
            borderRadius: "5px",
            marginBottom: "20px",
            fontSize: "16px",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          <FontAwesomeIcon icon={faCircleInfo} />
          &nbsp; Note! Allowed File Extensions:{" "}
          <strong>jpg, jpeg, png, gif, mp4</strong>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              border: "2px dashed #ccc",
              borderRadius: "5px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              position: "relative",
              minHeight: "300px",
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              multiple
            />
            {images.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {images.map((image, index) => (
                  <Card
                    key={index}
                    sx={{
                      maxWidth: 200,
                      borderRadius: "1px",
                      boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                      position: "relative",
                      overflow: "hidden",
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      src={URL.createObjectURL(image)}
                      alt={`Uploaded Preview ${index + 1}`}
                      sx={{
                        width: "100%",
                        height: "100px",
                        objectFit: "contain",
                        backgroundColor: "#f4f4f4",
                      }}
                    />

                    <IconButton
                      sx={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        borderRadius: "50%",
                        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 0, 0, 0.8)",
                          color: "white",
                        },
                      }}
                      onClick={() => handleRemoveImage(index)}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </IconButton>

                    <CardContent sx={{ textAlign: "center", padding: "8px" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                        }}
                        title={image.name}
                      >
                        {image.name.length > 15
                          ? `${image.name.substring(0, 12)}...`
                          : image.name}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <>
                <div className="d-flex flex-column justify-content-center align-items-center">
                  <img
                    src="https://img.freepik.com/free-vector/image-upload-concept-illustration_114360-798.jpg"
                    alt="Image_Upload"
                    style={{ width: "350px", height: "auto" }}
                  />
                  <p>Drag & drop your images here or click to upload</p>
                </div>
              </>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "15px",
              marginTop: "20px",
            }}
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                padding: "10px 20px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                "&:hover": {
                  backgroundColor: "#1565c0",
                },
              }}
              onClick={handleContainerClick}
            >
              <FontAwesomeIcon icon={faCirclePlus} />
              Add Images
            </Button>

            <Button
              variant="contained"
              sx={{
                backgroundColor: "#4caf50",
                color: "white",
                padding: "10px 20px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                "&:hover": {
                  backgroundColor: "#388e3c",
                },
              }}
              onClick={handleUploadFile}
              disabled={images.length === 0}
            >
              <FontAwesomeIcon icon={faCloudArrowUp} />
              Upload Images
            </Button>
          </div>

          {message.text && (
            <div
              style={{
                marginTop: "10px",
                padding: "10px",
                borderRadius: "5px",
                backgroundColor:
                  message.type === "success" ? "#d4edda" : "#f8d7da",
                color: message.type === "success" ? "#155724" : "#721c24",
              }}
            >
              {message.text}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MediaLibraryCreate;
